<script setup lang="ts">
import type { LocationListItem } from "@ca/db/locations";
import type { PresentableAircraft } from "@ca/types/aircraft";

import {
	computed,
	onMounted,
	ref,
	shallowRef,
	watch,
} from "vue";

import { debouncedWatch, useEventBus } from "@vueuse/core";
import mapboxgl from "mapbox-gl";
import { z } from "zod";

import { FlightStatePublishSchema } from "@ca/types/flight_state";

import { client } from "@/api";
import AircraftCards from "@/components/AircraftCards.vue";
import {
	airborneOnly, insuredOnly, searchTerm, 
} from "@/composables/filters";
import {
	computeHeading,
	makerElement,
	MAPBOX_TOKEN,
	popupContent,
} from "@/maps";

const map = shallowRef<mapboxgl.Map | null>(null);
mapboxgl.accessToken = MAPBOX_TOKEN;

const locations = ref<LocationListItem[]>([]);
const weatherSnapshot = ref<number | null>(null);
const ready = ref(false);

const fetchLocations = async () => {
	const response = await client.api.v1.aircraft.locations.get({
		query: {
			insured: insuredOnly.value,
		},
	});
	locations.value = response.data?.locations ?? [];
};

watch(insuredOnly, () => {
	fetchLocations();
});

const fetchWeatherSnapshot = async () => {
	try {
		const response = await client.api.v1.weather.snapshot.get();
		weatherSnapshot.value = response.data?.snapshot ?? null;
	} catch (error) {
		console.error("Failed to fetch weather snapshot:", error);
	}
};

fetchLocations();
fetchWeatherSnapshot();
onMounted(() => {
	map.value = new mapboxgl.Map({
		center: [-96.7970, 32.7767], // Dallas, TX coordinates
		container: "fleet-map",
		style: "mapbox://styles/mapbox/outdoors-v12",
		zoom: 12,
	});
	map.value.on("load", () => {
		if (map.value) {
			ready.value = true;
		}
	});
});

// Watch for weather snapshot to be available and add weather tiles
watch([ready, weatherSnapshot], () => {
	if (map.value && ready.value && weatherSnapshot.value && map.value.isStyleLoaded()) {
		// Check if source already exists
		if (!map.value.getSource("rainbow-weather")) {
			map.value.addSource("rainbow-weather", {
				type: "raster",
				tiles: [
					`/api/v1/weather/tiles/${weatherSnapshot.value}/0/{z}/{x}/{y}`,
				],
				tileSize: 256,
			});

			map.value.addLayer({
				id: "weather-layer",
				type: "raster",
				source: "rainbow-weather",
				paint: {
					"raster-opacity": 0.6,
				},
			});
		}
	}
}, {
	immediate: true,
});


const markerMap = new Map<string, mapboxgl.Marker>();
const locationMap = ref(new Map<string, LocationListItem>());
const bus = useEventBus<PresentableAircraft>("aircraft-panel");


const eventSchema = z.object({
	aircraft_id: z.string(),
	altitude: z.number().nullable(),
	heading: z.number().nullable(),
	location: z.object({
		coordinates: z.tuple([z.number(), z.number()]),
	}),
	speed: z.number().nullable(),
	timestamp: z.date(),
});



const stream = client.api.v1.aircraft.locations._latest.subscribe();

const handleLocationUpdate = (event: z.infer<typeof eventSchema>) => {
	const marker = markerMap.get(event.aircraft_id);
	if (marker) {
		marker.setLngLat(event.location.coordinates);
		const el = marker.getElement();
		const svg = el.querySelector("svg");
		if (svg) {
			svg.style.transform = `rotate(${computeHeading(event.heading)}deg)`;
		}
	}
	const loc = locationMap.value.get(event.aircraft_id);
	if (loc) {
		loc.heading = event.heading;
		loc.speed = event.speed;
		loc.altitude = event.altitude;
		loc.timestamp = new Date(event.timestamp);
		loc.location = event.location.coordinates;
		locationMap.value.set(event.aircraft_id, loc);
		const popup = marker?.getPopup();
		if (popup) {
			popup.setHTML(popupContent(loc));
		}
	}
};

const handleFlightStateUpdate = (event: z.infer<typeof FlightStatePublishSchema>) => {
	const loc = locationMap.value.get(event.aircraftId);
	if (loc) {
		loc.flightState = event.flightState;
		loc.flightStateUpdatedAt = new Date(event.flightStateUpdatedAt);
		locationMap.value.set(event.aircraftId, loc);
	}
	const marker = markerMap.get(event.aircraftId);
	if (marker && loc) {
		// Get the existing element
		const el = marker.getElement();
		const newEl = makerElement(loc.heading, loc.flightState);
		el.innerHTML = newEl.innerHTML;
		const popup = marker.getPopup();
		if (popup) {
			popup.setHTML(popupContent(loc));
		}
	}
};

stream.subscribe(({ data }) => {
	const { data: event, success } = eventSchema.safeParse(data);
	if (success) {
		handleLocationUpdate(event);
		return;
	}
	const {
		data: flightState,
		error: flightStateError, 
		success: flightStateSuccess,
	} = FlightStatePublishSchema.safeParse(data);
	if (flightStateSuccess) {
		handleFlightStateUpdate(flightState);
		return;
	}
	console.error("Invalid event", data, flightStateError);
});

const filteredTails = ref(new Set<string>());

const filteredLocations = computed(() => {
	return locations.value.filter((loc) => {
		const includeTail = searchTerm.value === "" || filteredTails.value.has(loc.tailNumber);
		if (airborneOnly.value) {
			return loc.flightState === "airborne" && includeTail;
		}
		return includeTail;
	});
});

const handlePopup = (ac: PresentableAircraft) => {
	const popupBtn = document.getElementById(`${ac.tailNumber}-popup-btn`);
	if (popupBtn) {
		popupBtn.addEventListener("click", () => {
			bus.emit(ac);
		});
	}
};


watch(filteredLocations, () => {
	const fleetMap = map.value;
	if (fleetMap) {
		// Remove any existing markers
		const markers = document.getElementsByClassName("mapboxgl-marker");
		while (markers[0]) {
			markers[0].remove();
		}
		// Calculate bounding box from all locations
		if (locations.value.length > 0) {
			const bounds = new mapboxgl.LngLatBounds();
			locations.value.forEach((loc) => {
				bounds.extend(loc.location);
			});
			
			// Fit map to bounds with some padding
			fleetMap.fitBounds(bounds, {
				animate: false,
				maxZoom: 12, // Prevent zooming in too far if markers are close together
				padding: 50,
			});
		}

		// Add new markers for each location
		filteredLocations.value.forEach((loc) => {
			const popup = new mapboxgl.Popup({
				offset: 25,
			}).setHTML(popupContent(loc));
			popup.on("open", () => {
				handlePopup({
					tailNumber: loc.tailNumber,
					make: loc.make,
					model: loc.model,
					year: loc.year,
					id: loc.aircraftId,
				});
			});

			const marker = new mapboxgl.Marker({
				element: makerElement(loc.heading, loc.flightState),
			})
				.setLngLat(loc.location)
				.setPopup(popup)
				.addTo(fleetMap);
			
			const el = marker.getElement();
			const svg = el.querySelector("svg");
			if (svg) {
				svg.style.transform = `rotate(${loc.heading ?? 0}deg)`;
			}
			
			markerMap.set(loc.aircraftId, marker);
			locationMap.value.set(loc.aircraftId, loc);
		});
	}
});



const selectedAircraft = ref<null | string>(null);
// Add a function to handle card clicks
const handleCardClick = ({ aircraftId, selected }: { aircraftId: string; selected: boolean }) => {

	selectedAircraft.value = selected ? aircraftId : null;

	const marker = markerMap.get(aircraftId);
	if (marker) {
		const popup = marker.getPopup();
	
		if (selected && popup && !popup.isOpen()) {
			marker.togglePopup();
		} 
		if (!selected && popup && popup.isOpen()) {
			marker.togglePopup();
		}
		if (selected) {
			map.value?.flyTo({
				center: marker.getLngLat(),
				duration: 1000,
				zoom: 13,
			});	
		}
	}
};

const selectedLocation = computed(() => {
	const loc = locationMap.value.get(selectedAircraft.value ?? "");
	if (loc) {
		return loc.location;
	}
	return null;
});

watch(selectedLocation, () => {
	if (selectedLocation.value) {
		map.value?.flyTo({
			center: selectedLocation.value,
		});
	}
});

debouncedWatch(searchTerm, async () => {
	const response = await client.api.v1.admin.aircraft.get({
		query: {
			tail: searchTerm.value,
		},
	});
	filteredTails.value = new Set(response.data?.aircraft.map((a) => a.tailNumber) ?? []);
}, {
	debounce: 250,
});

</script>

<template>
  <div class="flex h-screen">
    <div
      id="fleet-map"
      class="size-full"
    />
		  
		
    <div class="absolute bottom-4 right-5 left-14">
      <AircraftCards 
        :locations="filteredLocations" 
        @card-click="handleCardClick"
      />
    </div>
  </div>
</template>
